import { computed, Directive, effect, inject, resource, ResourceRef, signal, Signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  ApiException,
  AvailableTransitionsClient,
  FormDataDto,
  FormSchemaMiniDto,
  FormTemplateClient,
  isUnityValidationProblem,
  TransitionMiniDto,
  TransitionRequestDto,
  TransitionsClient,
  UnityValidationError,
  UnityWorkflowState,
} from '@unity/clients';
import { DetailFormService } from '@unity/services/detail-form.service';
import { FormSchemaService } from '@unity/services/form-schema.service';
import { LayoutService } from '@unity/services/layout.service';
import { PermissionService } from '@unity/services/permission.service';
import { ToasterService } from '@unity/services/toaster.service';
import { CanDeactivateGuard, UnityDto, UnityEntitlementPath } from '@unity/utils/types';
import { catchError, EMPTY, Observable, take } from 'rxjs';
import { SurveyModel } from 'survey-core';

@Directive() // See https://github.com/angular/angular/issues/37769
export abstract class AbstractManageSurveyComponent<TDto extends Readonly<UnityDto>> implements CanDeactivateGuard {
  private readonly formSchemasClient = inject(FormTemplateClient);
  private readonly detailFormService = inject(DetailFormService);
  private readonly formSchemaService = inject(FormSchemaService);
  private readonly toasterService = inject(ToasterService);
  private readonly permissionService = inject(PermissionService);
  protected readonly layoutService = inject(LayoutService);
  protected readonly transitionsClient = inject(TransitionsClient);
  protected readonly availableTransitionsClient = inject(AvailableTransitionsClient);

  /* Abstract */
  protected abstract resourceName: string;
  protected abstract editEntitlement: UnityEntitlementPath;
  protected abstract deleteEntitlement: UnityEntitlementPath;
  protected abstract transitionEntitlement: UnityEntitlementPath;
  protected abstract mainResource: ResourceRef<TDto | undefined>;

  protected abstract readonly $resourceId: Signal<number>;
  protected abstract readonly $formSchema: Signal<FormSchemaMiniDto | null | undefined>;

  protected abstract availableTransitionsFn$: (resourceId: number) => Observable<TransitionMiniDto[]>;
  protected abstract updateResourceFn$: (resourceId: number, data: FormDataDto, transition?: TransitionMiniDto) => Observable<unknown>;
  protected abstract deleteResourceFn$: (resourceId: number) => Observable<unknown>;
  protected abstract doTransitionFn$: (resourceId: number, transition: TransitionRequestDto) => Observable<unknown>;

  /* Signals */
  protected readonly $isEditing = signal<boolean>(false);
  protected readonly $isSaving = signal<boolean>(false);
  protected readonly $isDirty = signal<boolean>(false);

  /* Computed */
  protected readonly $surveyModel = computed(() => this.surveyModelResource.value());
  protected readonly $resource = computed(() => this.mainResource.value());
  protected readonly $isLoading = computed(() => this.mainResource.isLoading());

  protected readonly $isEntitledToEdit = computed(() => {
    const resource = this.$resource();
    const isEditableStateOrNotStateful = resource?.current_state == undefined || resource?.current_state?.is_editable;
    return isEditableStateOrNotStateful && this.permissionService.userIsEntitled(this.editEntitlement);
  });

  protected readonly $isEntitledToTransition = computed(() => {
    return this.permissionService.userIsEntitled(this.transitionEntitlement);
  });

  protected readonly $transitions = computed(() => {
    if (!this.availableTransitionsResource.hasValue()) {
      return [];
    }
    const workflow = this.availableTransitionsResource.value();
    return workflow;
  });

  protected readonly $transitionsThatRequireValidation = computed(() => {
    const availableTransitions = this.$transitions();
    return availableTransitions.filter(x => x.requires_validation === true);
  });

  protected readonly $transitionsThatDoNotRequireValidation = computed(() => {
    const availableTransitions = this.$transitions();
    return availableTransitions.filter(x => x.requires_validation === false);
  });

  /* Resources */
  private readonly formSchemaResource = rxResource({
    params: () => {
      const formSchema = this.$formSchema();
      const formSchemaId = formSchema?.id;
      return formSchemaId ? { formSchemaId } : undefined;
    },
    stream: ({ params: { formSchemaId } }) => this.formSchemasClient.getOneAsDetailed(formSchemaId),
  });

  private readonly surveyModelResource = resource({
    params: () => {
      const formSchema = this.formSchemaResource.value();
      const formSchemaData = formSchema?.data;
      return formSchemaData ? { formSchemaData } : undefined;
    },
    loader: ({ params: { formSchemaData } }) => this.formSchemaService.surveyModel(formSchemaData),
  });

  private availableTransitionsResource = rxResource({
    params: () => {
      const isEntitledToTransition = this.$isEntitledToTransition();
      const resourceId = this.$resourceId();
      return resourceId && isEntitledToTransition ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.availableTransitionsFn$(resourceId),
  });

  /* Methods */
  public constructor() {
    effect(() => {
      const isEditing = this.$isEditing();
      const isSaving = this.$isSaving();
      const surveyModel = this.$surveyModel();
      if (surveyModel) {
        surveyModel.readOnly = isSaving || !isEditing;
      }
    });

    effect(() => {
      const surveyModel = this.$surveyModel();
      if (surveyModel) {
        surveyModel.onValueChanged.add(() => {
          this.$isDirty.set(true);
        });
      }
    });

    effect(() => {
      const resource = this.$resource();
      const surveyModel = this.$surveyModel();
      if (surveyModel && resource) {
        surveyModel.data = this.getData(resource);
        this.$isDirty.set(false);
      }
    });
  }

  protected getData(resource: TDto): FormDataDto {
    return resource;
  }

  protected onSave(transition?: TransitionMiniDto) {
    const surveyModel = untracked(this.$surveyModel);
    const resource = untracked(this.$resource);

    if (!surveyModel) {
      return;
    }
    surveyModel.clearIncorrectValues(true);

    // TODO determine the correct way to decide if validation should be run or not.
    const isDraft = resource?.current_state?.enum_value == UnityWorkflowState.Draft;

    if (!isDraft || (transition && transition.requires_validation)) {
      surveyModel.validate(true, true, hasErrors => {
        if (!hasErrors) {
          this.updateResource(surveyModel, transition);
        }
      });
      return;
    }

    this.updateResource(surveyModel, transition);
  }

  protected onEdit() {
    this.$isEditing.set(true);
  }

  protected onCancel() {
    const resource = untracked(this.$resource);
    const surveyModel = untracked(this.$surveyModel);

    this.$isEditing.set(false);
    if (surveyModel && resource) {
      surveyModel.data = this.getData(resource);
      this.$isDirty.set(false);
    }
  }

  protected onDelete() {
    const resourceId = this.$resourceId();
    if (!resourceId) {
      throw new Error('Invalid page state, please reload');
    }
    this.detailFormService.deleteResource$(this.deleteResourceFn$(resourceId), this.resourceName).subscribe();
  }

  protected onWorkflow(transition: TransitionRequestDto) {
    const resourceId = this.$resourceId();
    if (!resourceId) {
      throw new Error('Invalid page state, please reload');
    }
    this.doTransitionFn$(resourceId, transition)
      .pipe(take(1))
      .subscribe(() => {
        this.mainResource.reload();
        this.availableTransitionsResource.reload();
      });
  }

  protected updateResource(surveyModel: SurveyModel, transition?: TransitionMiniDto) {
    const resourceId = untracked(this.$resourceId);

    this.$isSaving.set(true);

    this.updateResourceFn$(resourceId, surveyModel.data, transition)
      .pipe(
        catchError(e => {
          return this.onSubmitError(surveyModel, e);
        }),
        take(1)
      )
      .subscribe({
        next: () => {
          this.onSubmitSuccess();
        },
      });
  }

  private onSubmitSuccess() {
    this.toasterService.showInfo('Saved');
    this.mainResource.reload();
    this.availableTransitionsResource.reload();
    this.$isSaving.set(false);
    this.$isEditing.set(false);
  }

  private onSubmitError(surveyModel: SurveyModel, e: Error) {
    if (ApiException.isApiException(e) && isUnityValidationProblem(e.result)) {
      //handle server side errros
      this.handleServerValidationErrors(surveyModel, e.result.errors ?? {});
    } else {
      this.toasterService.showError('Error while saving changes', e, 'error');
    }

    this.$isSaving.set(false);
    return EMPTY;
  }

  private handleServerValidationErrors(surveyModel: SurveyModel, errors: Record<string, UnityValidationError[]>) {
    if (errors['']) {
      this.toasterService.showError('Form invalid', new Error(errors[''].join('. ')), 'warning');
    }

    for (const key of Object.keys(errors)) {
      const question = surveyModel.getQuestionByName(key);
      const errorMessage = errors[key].map(x => x.message).join('. ');
      if (question) {
        question.addError(errorMessage);
      } else {
        this.toasterService.showError('Form invalid', new Error(errorMessage), 'warning');
      }
    }
  }

  public canDeactivate() {
    const isDirty = untracked(this.$isDirty);

    if (isDirty) {
      return window.confirm('You have unsaved changes on this page. Are you sure you want to leave?');
    }

    return true;
  }
}
